import * as Tone from 'tone';

/**
 * Звук дыхательной практики (13.10.2026).
 *
 * Раньше на смену фазы звучал один короткий синусоидальный писк. Теперь — «чаша»:
 * мягкий тон с долгим затуханием и лёгким эхом на каждую смену фазы, у каждой фазы
 * своя нота. Синтезируется на лету, файлов нет.
 *
 * Был и второй режим, «волна» (шум через фильтр, нарастающий на вдохе). Слава послушал:
 * «звуки ужасные» — убран в тот же день. Синтезированный шум не возвращать; если нужен
 * звук дыхания, то только записанный.
 *
 * ⚠️ На iPhone с выключенным звонком (переключатель сбоку) звук из браузера не идёт —
 * это ограничение системы, а не поломка.
 */

export type BreathSoundMode = 'bell' | 'off';

const KEY = 'breathing_sound_mode';
const OLD_KEY = 'breathing_sound_enabled';

export function loadSoundMode(): BreathSoundMode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'bell' || v === 'off') return v;
    // прежняя настройка «звук выключен» сохраняется
    if (localStorage.getItem(OLD_KEY) === 'false') return 'off';
  } catch { /* хранилище недоступно — берём умолчание */ }
  // сюда же попадает сохранённая «волна» — режима больше нет
  return 'bell';
}

export function saveSoundMode(mode: BreathSoundMode) {
  try { localStorage.setItem(KEY, mode); } catch { /* не страшно */ }
}

export const SOUND_MODE_LABEL: Record<BreathSoundMode, string> = { bell: 'Чаша', off: 'Без звука' };

const BELL_NOTE: Record<string, string> = { inhale: 'G4', short_inhale: 'B4', hold: 'D4', exhale: 'C4' };

export class BreathSound {
  private bell: Tone.PolySynth | null = null;
  private reverb: Tone.Freeverb | null = null;
  private ready = false;
  mode: BreathSoundMode = 'bell';

  /** Вызывать из обработчика нажатия: браузер разрешает звук только после жеста. */
  async unlock() {
    if (this.ready) return;
    try {
      if (Tone.context.state !== 'running') await Tone.start();
      this.reverb = new Tone.Freeverb({ roomSize: 0.82, dampening: 2600, wet: 0.35 }).toDestination();
      this.bell = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: 'sine' },
        envelope: { attack: 0.02, decay: 1.6, sustain: 0, release: 2.2 },
        volume: -20,
      }).connect(this.reverb);
      this.ready = true;
    } catch (e) {
      console.warn('[breathSound] не удалось запустить звук:', e);
    }
  }

  /** Начало фазы. first — первая фаза цикла, она звучит чуть заметнее. */
  phase(type: string, _duration: number, first = false) {
    if (!this.ready || this.mode === 'off') return;
    this.bell?.triggerAttackRelease(BELL_NOTE[type] ?? 'C4', 1.2, Tone.now(), first ? 0.9 : 0.6);
  }

  /** Пауза: чаша затухает сама, глушить нечего */
  hush() { /* намеренно пусто */ }

  /** Финал: одна долгая «чаша» */
  finish() {
    if (!this.ready || this.mode === 'off') return;
    this.bell?.triggerAttackRelease(['C4', 'G4'], 2.2, Tone.now() + 0.2, 0.7);
  }

  dispose() {
    try {
      this.bell?.dispose();
      this.reverb?.dispose();
    } catch { /* уже освобождено */ }
    this.ready = false;
  }
}
